import { useEffect, useRef, useState } from 'react';

/**
 * Plays the royal fanfare when the king's profile opens.
 *
 * Autoplay caveat, which no amount of code removes: browsers refuse to start audio until the
 * document has been interacted with. In practice that means:
 *   - navigating here from anywhere else on the site → plays instantly (the document already has
 *     "sticky activation" from the click that brought them here), which is the common case;
 *   - a cold load straight into this URL (pasted link, new tab, refresh) → the play() promise is
 *     rejected, so we arm one-shot listeners and fire on the visitor's very next click, key press
 *     or scroll.
 */

const FANFARE_SRC = '/audio/royal-fanfare.mp3';

// Background flourish, not a foreground event — keep it well under the page's own volume budget.
const VOLUME = 0.35;

export const useRoyalFanfare = () => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = useState(false);
    /** True once the file is known to be missing or undecodable — used to hide the mute control. */
    const [unavailable, setUnavailable] = useState(false);

    useEffect(() => {
        const audio = new Audio(FANFARE_SRC);
        audio.volume = VOLUME;
        audio.preload = 'auto';
        audioRef.current = audio;

        const onEnded = () => setPlaying(false);
        // A missing or unplayable file should degrade to silence, not to a console full of noise.
        const onError = () => { setUnavailable(true); setPlaying(false); };
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('error', onError);

        let done = false;
        const cleanupGestureListeners = () => {
            window.removeEventListener('pointerdown', onGesture);
            window.removeEventListener('keydown', onGesture);
            window.removeEventListener('scroll', onGesture);
        };

        const attempt = () => {
            if (done) return;
            audio.play().then(
                () => { done = true; setPlaying(true); cleanupGestureListeners(); },
                () => { /* blocked — the gesture listeners below will retry */ }
            );
        };

        function onGesture() { attempt(); }

        attempt();
        // Armed only for the blocked case; the success path tears these down immediately.
        window.addEventListener('pointerdown', onGesture, { once: false });
        window.addEventListener('keydown', onGesture, { once: false });
        window.addEventListener('scroll', onGesture, { once: false });

        return () => {
            cleanupGestureListeners();
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('error', onError);
            // Never let audio outlive the page that started it.
            audio.pause();
            audio.src = '';
            audioRef.current = null;
        };
    }, []);

    const stop = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.pause();
        audio.currentTime = 0;
        setPlaying(false);
    };

    return { playing, stop, unavailable };
};
